import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, X, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import { SectionHeading } from "@/components/ui/section-heading";
import { SkeletonImage, ProgressiveLoader } from "@/components/ui/luxury-skeleton";
import { motion, AnimatePresence } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { cn } from "@/lib/utils";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import { useBooking } from "@/hooks/use-booking-context";

import { propertyMedia } from "@/lib/property-media";
import { mobilePhotoPosition } from "@/lib/property-photo-focus";
import { useMediaPreference } from "./property-media";

interface Photo {
  video?: { desktop?: string; mobile?: string; mobilePoster: string };
  id: string;
  url: string;
  alt: string;
  sort_order?: number;
  is_featured?: boolean;
}

const DEFAULT_PHOTOS: Photo[] = [
  {
    id: "1",
    url: "https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600",
    alt: "The Kualoa Beach House - Beach House living room with ocean views",
    is_featured: true,
  },
  {
    id: "2",
    url: "https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
    alt: "The Kualoa Beach House - Master bedroom with tropical decor",
    is_featured: true,
  },
  {
    id: "3",
    url: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
    alt: "The Kualoa Beach House - Full kitchen with modern appliances",
  },
  {
    id: "4",
    url: "https://images.unsplash.com/photo-1564501049412-61c2a3083791?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
    alt: "The Kualoa Beach House - Infinity pool with ocean views at sunset",
  },
  {
    id: "5",
    url: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
    alt: "The Kualoa Beach House - Luxury bathroom with rainfall shower",
  },
  {
    id: "6",
    url: "https://images.unsplash.com/photo-1600585152915-d208bec867a1?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400",
    alt: "The Kualoa Beach House - Outdoor dining area with ocean views",
  },
];

export function PhotoGallery({
  propertySlug,
  heading,
  lede,
}: { propertySlug?: string; heading?: string; lede?: string } = {}) {
  const { propertySlug: contextSlug } = useBooking();
  const slug = propertySlug || contextSlug;
  const preference = useMediaPreference();
  const galleryRef = useRef<HTMLElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [announceText, setAnnounceText] = useState("");
  const [isPointerDevice, setIsPointerDevice] = useState(false);

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const desktopGalleryRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  // Mark component as mounted (client-side only) to enable portal rendering
  useEffect(() => { setIsMounted(true); }, []);

  // Detect pointer (mouse/trackpad) vs touch device for zoom capability
  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    setIsPointerDevice(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsPointerDevice(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    skipSnaps: false,
    watchDrag: (_api, event) => !(event.target instanceof Element && event.target.closest("video")),
  });
  const [emblaIndex, setEmblaIndex] = useState(0);
  useEffect(() => {
    const firstVideo = () => { setActiveIndex(0); emblaApi?.scrollTo(0, true); };
    window.addEventListener('property-gallery:video', firstVideo);
    return () => window.removeEventListener('property-gallery:video', firstVideo);
  }, [emblaApi]);

  const { data: photos = [], isLoading } = useQuery<Photo[]>({
    queryKey: slug ? ["/api/photos", { property: slug }] : ["/api/photos"],
  });

  const displayPhotos = useMemo(() => {
    const source = photos.length > 0 ? photos : DEFAULT_PHOTOS;
    const sorted = [...source].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const media = propertyMedia(slug);
    return media ? [{id: `video-${slug}`, url: media.desktopImage, alt: media.description, video: {desktop: media.desktopVideo, mobile: media.mobileVideo, mobilePoster: media.mobileImage}}, ...sorted] : sorted;
  }, [photos, slug]);

  useEffect(() => {
    galleryRef.current?.querySelectorAll("video").forEach(video => video.pause());
  }, [activeIndex, emblaIndex, isLightboxOpen, preference?.mobile]);

  const galleryVideo = (photo: Photo, active: boolean) => {
    if (!photo.video || !preference || !active) return null;
    return <video key={`${photo.id}-${preference.mobile}`} controls playsInline muted preload="none"
      src={preference.mobile ? photo.video.mobile : photo.video.desktop}
      poster={preference.mobile ? photo.video.mobilePoster : photo.url}
      aria-label="Property video" aria-description={photo.alt}
      className="absolute inset-0 h-full w-full object-contain bg-black"
      onClick={event => event.stopPropagation()} onKeyDown={event => { if (["ArrowLeft", "ArrowRight", " ", "Enter"].includes(event.key)) event.stopPropagation(); }} />;
  };

  const openLightbox = useCallback(
    (index: number) => {
      lastFocusedRef.current = document.activeElement as HTMLElement;
      setLightboxIndex(index);
      setIsZoomed(false);
      setIsLightboxOpen(true);
      setAnnounceText(
        `Opened image ${index + 1} of ${displayPhotos.length}: ${displayPhotos[index]?.alt || "Property image"}`
      );
    },
    [displayPhotos]
  );

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setEmblaIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi]);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    setIsZoomed(false);
    setAnnounceText("Gallery closed");
    setTimeout(() => lastFocusedRef.current?.focus(), 100);
  }, []);

  const nextLightbox = useCallback(() => {
    const next = (lightboxIndex + 1) % displayPhotos.length;
    setLightboxIndex(next);
    setIsZoomed(false);
    setAnnounceText(
      `Image ${next + 1} of ${displayPhotos.length}: ${displayPhotos[next]?.alt || "Property image"}`
    );
  }, [lightboxIndex, displayPhotos]);

  const prevLightbox = useCallback(() => {
    const prev = (lightboxIndex - 1 + displayPhotos.length) % displayPhotos.length;
    setLightboxIndex(prev);
    setIsZoomed(false);
    setAnnounceText(
      `Image ${prev + 1} of ${displayPhotos.length}: ${displayPhotos[prev]?.alt || "Property image"}`
    );
  }, [lightboxIndex, displayPhotos]);

  // Desktop main gallery navigation
  const prevMain = useCallback(
    () => setActiveIndex((i) => (i - 1 + displayPhotos.length) % displayPhotos.length),
    [displayPhotos.length]
  );
  const nextMain = useCallback(
    () => setActiveIndex((i) => (i + 1) % displayPhotos.length),
    [displayPhotos.length]
  );

  // Keyboard: lightbox navigation (when lightbox is open)
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const dialog = closeButtonRef.current?.closest('[role="dialog"]');
        const focusable = Array.from(dialog?.querySelectorAll<HTMLElement>('button, video[controls], a[href], [tabindex="0"]') || []).filter(el=>el.getClientRects().length);
        const first = focusable[0], last = focusable.at(-1);
        if (e.shiftKey && document.activeElement === first) {e.preventDefault();last?.focus();}
        else if (!e.shiftKey && document.activeElement === last) {e.preventDefault();first?.focus();}
      }
      if (e.key === "ArrowLeft") { e.preventDefault(); prevLightbox(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); nextLightbox(); }
      else if (e.key === "Escape") { e.preventDefault(); closeLightbox(); }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isLightboxOpen, prevLightbox, nextLightbox, closeLightbox]);

  // Keyboard: desktop gallery navigation (when gallery is focused, lightbox is closed)
  const handleDesktopGalleryKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isLightboxOpen) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevMain();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        nextMain();
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openLightbox(activeIndex);
      }
    },
    [isLightboxOpen, prevMain, nextMain, openLightbox, activeIndex]
  );

  // Focus close button when lightbox opens
  useEffect(() => {
    if (isLightboxOpen) closeButtonRef.current?.focus();
  }, [isLightboxOpen]);

  // Lock body scroll while lightbox is open
  useEffect(() => {
    if (!isLightboxOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isLightboxOpen]);

  const videoTouch = useRef(false);
  const handleLightboxTouchStart = (e: React.TouchEvent) => {
    videoTouch.current = e.target instanceof Element && Boolean(e.target.closest("video"));
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const handleLightboxTouchEnd = (e: React.TouchEvent) => {
    if (videoTouch.current) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
    if (Math.abs(dx) > 50 && dy < 100) {
      if (dx < 0) nextLightbox();
      else prevLightbox();
    }
  };

  useEffect(() => {
    const strip = thumbnailStripRef.current;
    if (!strip) return;
    const thumb = strip.children[activeIndex] as HTMLElement;
    if (thumb) {
      // Scroll the thumbnail strip horizontally only — never use scrollIntoView,
      // which also scrolls the whole page vertically (block: "nearest") and would
      // jump the page down to the gallery on initial mount.
      const targetLeft =
        thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2;
      strip.scrollTo({ left: targetLeft, behavior: "smooth" });
    }
  }, [activeIndex]);

  const skeletonContent = (
    <section id="gallery" className="py-20 bg-shell">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="h-8 w-64 mx-auto mb-4 bg-gradient-to-r from-sand via-sand-deep to-sand animate-shimmer bg-[length:200%_100%] rounded-sm" />
          <div className="h-6 w-96 max-w-[90vw] mx-auto bg-gradient-to-r from-sand via-sand to-sand animate-shimmer bg-[length:200%_100%] rounded-sm" />
        </div>
        {/* Mobile skeleton: carousel only */}
        <div className="md:hidden">
          <div className="aspect-[9/16] w-full animate-pulse bg-sand" />
          <div className="flex gap-2 justify-center mt-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="w-2 h-2 rounded-full bg-sand" />
            ))}
          </div>
        </div>
        {/* Desktop skeleton: main image + thumbnail strip */}
        <div className="hidden md:block max-w-5xl mx-auto space-y-4">
          <SkeletonImage aspectRatio="3/2" className="w-full rounded-sm border border-sand-deep" variant="shimmer" />
          <div className="flex gap-2">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="flex-shrink-0 w-20 h-14 rounded-sm bg-gradient-to-r from-sand via-sand to-sand animate-shimmer bg-[length:200%_100%]"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );

  const galleryContent = (
    <section
      id="gallery"
      ref={galleryRef}
      className="py-20 bg-sand section-stable border-t border-sand-deep"
      aria-labelledby="gallery-heading"
      role="region"
    >
      <div className="container mx-auto px-4">
        <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
          {announceText}
        </div>

        <ScrollReveal className="mb-12">
          <SectionHeading
            align="center"
            eyebrow="Gallery"
            title={heading || "The Kualoa Beach House: Stunning Views & Spaces"}
            lede={lede || "Discover every corner of our tropical paradise, from the stunning interior spaces to the private beach access."}
            id="gallery-heading"
          />
        </ScrollReveal>

        {/* ══════════════════════════════════════════════════════
            MOBILE LAYOUT
        ══════════════════════════════════════════════════════ */}
        <div className="-mx-4 md:hidden">

          {/* ── MOBILE: Embla swipe carousel ──────────────────── */}
          <div
            className="overflow-hidden"
            ref={emblaRef}
            style={{ borderRadius: "2px" }}
            aria-label={`Property gallery, ${displayPhotos.length} photos and videos. Swipe to browse.`}
          >
            <div className="flex">
              {displayPhotos.map((photo, index) => (
                <div
                  key={photo.id}
                  style={{ flex: "0 0 100%" }}
                  className="relative cursor-pointer"
                  onClick={() => { if (!photo.video) openLightbox(index); }}
                  role={photo.video ? "group" : "button"}
                  tabIndex={photo.video ? undefined : 0}
                  aria-label={photo.video ? "Property video" : `Open image ${index + 1} of ${displayPhotos.length}: ${photo.alt}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openLightbox(index);
                    }
                  }}
                >
                  <div
                    className="relative overflow-hidden border border-sand-deep rounded-sm"
                    style={{ aspectRatio: "9/16" }}
                  >
                    {/* An active video owns its poster; a second cover image shows through its letterbox. */}
                    {!(photo.video && preference?.mobile && emblaIndex === index && !isLightboxOpen) && <img
                      src={photo.video ? (preference?.mobile ? photo.video.mobilePoster : undefined) : optimizedUrl(photo.url, 2560)}
                      srcSet={photo.video ? undefined : optimizedSrcSet(photo.url, [1200, 1920, 2560, 3840])}
                      sizes="320vw"
                      alt={photo.alt}
                      className="w-full h-full object-cover"
                      style={{ objectPosition: mobilePhotoPosition(photo.url) }}
                      width={1200}
                      height={800}
                      decoding="async"
                      loading={index === 0 ? "eager" : "lazy"}
                      {...({ fetchpriority: index === 0 ? "high" : "auto" } as any)}
                    />}
                    {galleryVideo(photo, Boolean(preference?.mobile && emblaIndex === index && !isLightboxOpen))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Position counter + large prev/next controls. Replaces per-image dots so
              every tap target comfortably meets the 44px accessibility minimum. */}
          <div className="flex items-center justify-center gap-4 mt-4">
            <button
              type="button"
              onClick={() => emblaApi?.scrollPrev()}
              aria-label="Previous image"
              className="w-11 h-11 rounded-full border border-sand-deep text-ink flex items-center justify-center hover:bg-shell transition-colors"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <span
              className="text-luxury-sm font-serif text-ink tabular-nums min-w-[4.5rem] text-center"
              aria-live="polite"
              data-testid="gallery-position"
            >
              {emblaIndex + 1} / {displayPhotos.length}
            </span>
            <button
              type="button"
              onClick={() => emblaApi?.scrollNext()}
              aria-label="Next image"
              className="w-11 h-11 rounded-full border border-sand-deep text-ink flex items-center justify-center hover:bg-shell transition-colors"
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════
            TABLET + DESKTOP LAYOUT
        ══════════════════════════════════════════════════════ */}
        <div className="hidden md:block max-w-5xl mx-auto">

          {/* ── DESKTOP: Main image + thumbnail strip ─────────── */}
          <div
            ref={desktopGalleryRef}
            tabIndex={0}
            onKeyDown={handleDesktopGalleryKeyDown}
            aria-label={`Photo gallery. Use arrow keys to navigate. Press Enter to open full screen. Image ${activeIndex + 1} of ${displayPhotos.length}.`}
            style={{ outline: "none" }}
          >
            {/* Main image */}
            <div className="relative group mb-4">
              {/* A plain container: it holds the previous/next buttons, so it
                  cannot itself be a button. Clicking the photo still opens the
                  lightbox for mouse users, and the control below gives keyboard
                  users a real, focusable way to do the same thing. */}
              <div
                className="relative overflow-hidden rounded-sm cursor-zoom-in border border-sand-deep"
                style={{ aspectRatio: "3/2" }}
                onClick={() => { if (!displayPhotos[activeIndex]?.video) openLightbox(activeIndex); }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {displayPhotos[activeIndex]?.video ? galleryVideo(displayPhotos[activeIndex], Boolean(preference && !preference.mobile && !isLightboxOpen)) : <motion.img
                    key={activeIndex}
                    src={optimizedUrl(displayPhotos[activeIndex]?.url || "", 1200)}
                    srcSet={optimizedSrcSet(displayPhotos[activeIndex]?.url || "", [768, 1200, 1600, 1920, 2560])}
                    sizes="(min-width: 1024px) 1024px, 100vw"
                    alt={displayPhotos[activeIndex]?.alt}
                    className="absolute inset-0 w-full h-full object-cover"
                    width={1200}
                    height={800}
                    decoding="async"
                    {...({ fetchpriority: activeIndex === 0 ? "high" : "auto" } as any)}
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.28, ease: "easeOut" }}
                    loading="eager"
                    draggable={false}
                  />}
                </AnimatePresence>

                {/* Hover tint, and the real control that opens the lightbox. */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 pointer-events-none" aria-hidden="true" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openLightbox(activeIndex); }}
                  className="absolute top-4 right-4 z-10 inline-flex min-h-[44px] items-center rounded-full bg-shell px-4 py-2 font-serif text-luxury-sm text-ink opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  aria-label={`View full screen: ${displayPhotos[activeIndex]?.alt || "photo"}`}
                  data-testid="gallery-expand"
                >
                  Click to expand
                </button>

                {/* Image counter */}
                <div className="absolute top-4 left-4 bg-shell text-ink px-3 py-1 rounded-full text-luxury-sm font-serif" aria-hidden="true">
                  {activeIndex + 1} / {displayPhotos.length}
                </div>

                {/* Arrow navigation — always visible on desktop/tablet */}
                {displayPhotos.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevMain();
                      }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-shell/80 hover:bg-shell backdrop-blur-sm text-ink flex items-center justify-center transition-colors duration-200 focus:outline-none border border-sand-deep shadow-none"
                      aria-label="Previous image"
                      data-testid="gallery-prev"
                    >
                      <ChevronLeft className="h-6 w-6" aria-hidden="true" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextMain();
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-shell/80 hover:bg-shell backdrop-blur-sm text-ink flex items-center justify-center transition-colors duration-200 focus:outline-none border border-sand-deep shadow-none"
                      aria-label="Next image"
                      data-testid="gallery-next"
                    >
                      <ChevronRight className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Thumbnail strip — outer div clips page overflow; inner div handles horizontal scroll */}
            <div className="w-full overflow-hidden">
            <div
              ref={thumbnailStripRef}
              className="flex gap-2 pb-1"
              role="listbox"
              aria-label="Photo thumbnails"
              style={{ overflowX: "auto", scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}
            >
              {displayPhotos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => setActiveIndex(index)}
                  className={cn(
                    "relative flex-shrink-0 rounded-sm overflow-hidden transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-ink focus:ring-offset-1 border border-sand-deep",
                    "w-20 h-14 lg:w-24 lg:h-16",
                    index === activeIndex
                      ? "ring-1 ring-ink ring-offset-1 opacity-100"
                      : "opacity-60 hover:opacity-100 hover:border-ink"
                  )}
                  role="option"
                  aria-selected={index === activeIndex}
                  aria-label={photo.video ? "Watch property video" : `View ${photo.alt}`}
                  data-testid={`thumbnail-${index}`}
                >
                  {photo.video && <Play className="absolute inset-0 z-10 m-auto h-6 w-6 fill-white text-ink" aria-hidden="true" />}
                  <img
                    src={optimizedUrl(photo.url, 160)}
                    alt={photo.alt}
                    className="w-full h-full object-cover"
                    width={96}
                    height={64}
                    decoding="async"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
            </div>{/* /w-full overflow-hidden thumbnail wrapper */}
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <>
      <ProgressiveLoader isLoading={isLoading} skeleton={skeletonContent} delay={0.1}>
        {galleryContent}
      </ProgressiveLoader>

      {/* ── LIGHTBOX — custom portal overlay (bypasses Radix positioning) ── */}
      {isMounted && createPortal(
        <AnimatePresence>
          {isLightboxOpen && (
            <motion.div
              key="lightbox"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby="lightbox-title"
              aria-describedby="lightbox-desc"
              className="fixed inset-0 z-[9999] flex flex-col overflow-y-auto bg-shell"
              onTouchStart={handleLightboxTouchStart}
              onTouchEnd={handleLightboxTouchEnd}
            >
              {/* Screen-reader title & description */}
              <h2 id="lightbox-title" className="sr-only">
                Property Gallery: Item {lightboxIndex + 1} of {displayPhotos.length}
              </h2>
              <p id="lightbox-desc" className="sr-only">
                Swipe left or right to navigate images. Use arrow keys to navigate. Press Escape to close.
                {isPointerDevice ? " Click image to zoom." : ""}
              </p>

              {/* ── Top bar (counter + close) ── */}
              <div
                className="sticky top-0 z-20 flex items-center justify-center flex-shrink-0 h-14 bg-shell"
                style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
              >
                {/* Counter */}
                <div className="bg-shell text-ink px-4 py-1.5 rounded-full text-luxury-sm font-serif pointer-events-none">
                  {lightboxIndex + 1} / {displayPhotos.length}
                </div>
                {/* Close button */}
                <Button
                  ref={closeButtonRef}
                  variant="ghost"
                  size="sm"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink hover:bg-ink/10 rounded-full w-10 h-10 p-0 flex items-center justify-center"
                  onClick={closeLightbox}
                  aria-label="Close photo gallery"
                  data-testid="lightbox-close"
                >
                  <X className="h-5 w-5" aria-hidden="true" />
                </Button>
              </div>

              {/* ── Main image area — fills all remaining vertical space ── */}
              <div
                className={cn(
                  "relative flex-none aspect-[9/16] w-full flex items-center justify-center md:aspect-auto md:flex-1 md:min-h-0 md:px-14",
                  isPointerDevice
                    ? isZoomed
                      ? "cursor-zoom-out overflow-auto"
                      : "cursor-zoom-in overflow-hidden"
                    : "overflow-hidden"
                )}
                onClick={() => { if (isPointerDevice && !preference?.mobile && !displayPhotos[lightboxIndex]?.video) setIsZoomed((z) => !z); }}
              >
                {/* Prev arrow */}
                {displayPhotos.length > 1 && (
                  <button
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-shell/85 hover:bg-shell backdrop-blur-sm text-ink flex items-center justify-center focus:outline-none border border-ink/20"
                    onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
                    aria-label="Previous image"
                    data-testid="lightbox-prev"
                  >
                    <ChevronLeft className="h-7 w-7" aria-hidden="true" />
                  </button>
                )}

                {/* Photo */}
                <AnimatePresence mode="wait" initial={false}>
                  {displayPhotos[lightboxIndex]?.video ? galleryVideo(displayPhotos[lightboxIndex], true) : <motion.img
                    key={lightboxIndex}
                    src={optimizedUrl(displayPhotos[lightboxIndex]?.url || "", 3840, 90)}
                    alt={displayPhotos[lightboxIndex]?.alt || "Property photo"}
                    className={cn(
                      "w-full h-full object-cover md:w-auto md:h-auto md:max-w-full md:object-contain select-none md:rounded",
                      isZoomed ? "max-h-none" : "max-h-full"
                    )}
                    style={{
                      objectPosition: preference?.mobile ? mobilePhotoPosition(displayPhotos[lightboxIndex]?.url || "") : undefined,
                      transform: isZoomed ? "scale(2)" : "scale(1)",
                      transition: "transform 0.3s ease",
                      transformOrigin: "center center",
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    draggable={false}
                    data-testid="lightbox-image"
                    loading="eager"
                    onError={(e) => {
                      const t = e.target as HTMLImageElement;
                      t.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='400' height='300' fill='%23333'/%3E%3Ctext x='200' y='155' text-anchor='middle' fill='%23888' font-size='14' font-family='sans-serif'%3EImage unavailable%3C/text%3E%3C/svg%3E";
                    }}
                  />}
                </AnimatePresence>

                {/* Next arrow */}
                {displayPhotos.length > 1 && (
                  <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-shell/85 hover:bg-shell backdrop-blur-sm text-ink flex items-center justify-center focus:outline-none border border-ink/20"
                    onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
                    aria-label="Next image"
                    data-testid="lightbox-next"
                  >
                    <ChevronRight className="h-7 w-7" aria-hidden="true" />
                  </button>
                )}
              </div>

              {/* ── Caption bar ── */}
              <div
                className="flex-shrink-0 px-6 py-3 text-center"
                style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))" }}
              >
                <p className="text-ink-soft text-luxury-sm font-serif truncate">
                  {displayPhotos[lightboxIndex]?.alt || ""}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
